import { faBatteryLow } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ILocationResponse } from '@kcftech/prometheus-client';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { HardwareIcon, HardwareIconType } from 'components/svg_icon/HardwareIcon';
import { useFeatureFlag } from 'configcat-react';
import { useLocationHardwareStatus } from 'custom_hooks/data_hooks/location/useLocationHardwareStatus';
import {
    ActionButton,
    Card,
    CardContent,
    Container,
    Count,
    CountContainer,
    Grid,
    HardwareInfo,
    HardwareType,
    IconContainer,
    LabelContainer,
    NoHardwareMessage,
    Title,
    TotalCount,
} from 'pages/Location/hardware_status/styled';
import { forwardRef, memo, useImperativeHandle, useMemo } from 'react';
import { getHardwareOverviewRoute, HardwareOverviewTab } from 'utils/RouteHelper';

type HardwareTypeConfig = {
    type: string;
    label: string;
    icon: HardwareIconType;
    route: HardwareOverviewTab;
    onlineCount: number;
    offlineCount: number;
    totalCount: number;
    action?: {
        icon: React.ReactNode;
        href: string;
        ariaLabel: string;
    };
};

type HardwareMappingConfig = {
    label: string;
    icon: HardwareIconType;
    route: HardwareOverviewTab;
    actionConfig?: {
        icon: React.ReactNode;
        getHref: (locationId: string) => string;
        ariaLabel: string;
    };
};

// Define hardware mappings for consistent display
const HARDWARE_MAPPINGS: Record<string, HardwareMappingConfig> = {
    baseStations: {
        label: 'Base Stations',
        icon: 'baseStation',
        route: 'baseStations',
    },
    repeaters: {
        label: 'Repeaters',
        icon: 'repeater',
        route: 'repeaters',
    },
    sensors: {
        label: 'Sensors',
        icon: 'sensor',
        route: 'sensors',
        actionConfig: {
            icon: <FontAwesomeIcon icon={faBatteryLow} />,
            getHref: (locationId) =>
                getHardwareOverviewRoute(locationId, 'sensors', {
                    field: 'BatteryVoltage',
                    direction: 'asc',
                }),
            ariaLabel: 'View low battery sensors',
        },
    },
    hubs: {
        label: 'IoT Hubs',
        icon: 'hub',
        route: 'hubs',
    },
    piezoSensors: {
        label: 'Piezo Sensing',
        icon: 'piezoSensing',
        route: 'piezo',
    },
};

export type LocationHardwareStatusWidgetProps = {
    location: ILocationResponse;
};

export type LocationHardwareStatusWidgetRef = {
    refetch: () => Promise<unknown>;
};

function LocationHardwareStatusWidget(
    { location }: LocationHardwareStatusWidgetProps,
    ref: React.ForwardedRef<LocationHardwareStatusWidgetRef>,
) {
    const { value: showLowBatteryButton } = useFeatureFlag(
        'overview_location_showLowBatteryButton',
        false,
    );

    const {
        data: hardwareStatus,
        isFetching,
        refetch,
    } = useLocationHardwareStatus(location.Id);

    // Expose refetch method to parent component
    useImperativeHandle(ref, () => ({
        refetch,
    }));

    // Transform API data to component format
    const availableHardware = useMemo(() => {
        if (!hardwareStatus) {
            return [];
        }

        const hardwareMap = {
            baseStations: hardwareStatus.Collectors,
            repeaters: hardwareStatus.Repeaters,
            sensors: hardwareStatus.Nodes,
            hubs: hardwareStatus.Hubs,
            piezoSensors: hardwareStatus.PiezoNodes,
        };

        return Object.entries(hardwareMap)
            .filter(([_, data]) => data.TotalCount > 0)
            .map(([type, data]) => {
                const mapping = HARDWARE_MAPPINGS[type];

                // Create the action from mapping configuration
                const action = mapping.actionConfig
                    ? {
                          icon: mapping.actionConfig.icon,
                          href: mapping.actionConfig.getHref(location.Id),
                          ariaLabel: mapping.actionConfig.ariaLabel,
                      }
                    : undefined;

                return {
                    type,
                    label: mapping.label,
                    icon: mapping.icon,
                    route: mapping.route,
                    onlineCount: data.OnlineCount,
                    offlineCount: data.OfflineCount,
                    totalCount: data.TotalCount,
                    action,
                };
            })
            .filter(Boolean) satisfies HardwareTypeConfig[];
    }, [hardwareStatus, location.Id]);

    // Empty state - don't render anything
    if (availableHardware.length === 0 && !isFetching) {
        return (
            <Container>
                <Title>Online Assigned Hardware</Title>
                <Grid>
                    <NoHardwareMessage>
                        No hardware found at this location
                    </NoHardwareMessage>
                </Grid>
            </Container>
        );
    }

    return (
        <Container>
            <Title>Online Assigned Hardware</Title>
            <SpinnerArea isLoading={isFetching}>
                <Grid>
                    {availableHardware.map((hardware) => {
                        const href = getHardwareOverviewRoute(
                            location.Id,
                            hardware.route,
                        );

                        return (
                            <Card
                                key={hardware.type}
                                to={href}
                                target="_blank"
                                aria-label={`${hardware.label}: ${hardware.onlineCount} online out of ${hardware.totalCount} total`}
                            >
                                <CardContent>
                                    <HardwareInfo>
                                        <LabelContainer>
                                            <HardwareType>{hardware.label}</HardwareType>
                                            {hardware.action ? (
                                                showLowBatteryButton ? (
                                                    <ActionButton
                                                        href={hardware.action.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label={
                                                            hardware.action.ariaLabel
                                                        }
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    >
                                                        {hardware.action.icon}
                                                    </ActionButton>
                                                ) : null
                                            ) : null}
                                        </LabelContainer>
                                        <CountContainer>
                                            <Count>{hardware.onlineCount}</Count>
                                            <TotalCount>
                                                / {hardware.totalCount}
                                            </TotalCount>
                                        </CountContainer>
                                    </HardwareInfo>
                                    <IconContainer>
                                        <HardwareIcon type={hardware.icon} />
                                    </IconContainer>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Grid>
            </SpinnerArea>
        </Container>
    );
}

const memoLocationHardwareStatusWidget = memo(
    forwardRef<LocationHardwareStatusWidgetRef, LocationHardwareStatusWidgetProps>(
        LocationHardwareStatusWidget,
    ),
);

export { memoLocationHardwareStatusWidget as LocationHardwareStatusWidget };
